import React from "react";
import type { ModeRendererProps } from "./types";

export function VasarelyIpccMode({ brand, today, Copy }: ModeRendererProps) {
  // Namespaced IDs to avoid collisions
  const idBase = React.useMemo(() => {
    try {
      const url = typeof window !== "undefined" ? new URL(window.location.href) : null;
      const param = url
        ? url.searchParams.get("id") || url.searchParams.get("uid") || url.searchParams.get("modeId") || url.searchParams.get("mode_id")
        : null;
      return param ? `ipcc_${param}` : `ipcc_${Math.random().toString(36).slice(2, 8)}`;
    } catch {
      return `ipcc_${Math.random().toString(36).slice(2, 8)}`;
    }
  }, []);
  const gradId = `${idBase}_grad`;
  const glowId = `${idBase}_glow`;
  const arrowId = `${idBase}_arrow`;

  // Consolidated defs
  const defs = (
    <defs>
      <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00BCD4" />
        <stop offset="35%" stopColor="#4CAF50" />
        <stop offset="65%" stopColor="#FFC107" />
        <stop offset="100%" stopColor="#F44336" />
      </linearGradient>
      <radialGradient id={glowId} cx="50%" cy="50%">
        <stop offset="0%" stopColor="#FFC107" stopOpacity={0.5} />
        <stop offset="100%" stopColor="#00BCD4" stopOpacity={0} />
      </radialGradient>
      <marker id={arrowId} markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
        <polygon points="0 0, 10 3, 0 6" fill={`url(#${gradId})`} />
      </marker>
    </defs>
  );

  // Heatmaps to gradients: temperature anomalies
  const heatmapGradients = [];
  for (let row = 0; row < 16; row++) {
    for (let col = 0; col < 12; col++) {
      const x = 80 + col * 55;
      const y = 120 + row * 55;
      const temp = (row / 15) * 0.7 + (Math.sin(col * 0.5) * 0.15 + 0.15);
      const color = temp < 0.33 ? "#00BCD4" : temp < 0.66 ? "#FFC107" : "#F44336";
      heatmapGradients.push(
        <rect
          key={`heat-${row}-${col}`}
          x={x - 20}
          y={y - 20}
          width={40}
          height={40}
          fill={color}
          opacity={0.3 + temp * 0.3}
          rx={4}
        />
      );
    }
  }

  // Circulation loops: ocean/atmospheric currents
  const circulationLoops = [];
  for (let i = 0; i < 8; i++) {
    const angle = (i * Math.PI * 2) / 8;
    const radius = 180 + (i % 3) * 30;
    const points = [];
    for (let j = 0; j < 36; j++) {
      const a = angle + (j * Math.PI * 2) / 36;
      const r = radius + Math.sin(j * 3) * 15;
      points.push(`${397 + r * Math.cos(a)},${561.5 + r * Math.sin(a)}`);
    }
    circulationLoops.push(
      <polyline
        key={`loop-${i}`}
        points={points.join(" ")}
        fill="none"
        stroke={`url(#${gradId})`}
        strokeWidth={2}
        opacity={0.4}
      />
    );
  }

  // Cryosphere grids: ice sheet monitoring
  const cryosphereGrids = [];
  for (let i = 0; i < 18; i++) {
    const y = 100 + i * 52;
    cryosphereGrids.push(
      <line
        key={`cryo-h-${i}`}
        x1={70}
        y1={y}
        x2={724}
        y2={y}
        stroke="#00BCD4"
        strokeWidth={1}
        opacity={0.2}
      />
    );
  }
  for (let i = 0; i < 13; i++) {
    const x = 70 + i * 52;
    cryosphereGrids.push(
      <line
        key={`cryo-v-${i}`}
        x1={x}
        y1={100}
        x2={x}
        y2={1023}
        stroke="#00BCD4"
        strokeWidth={1}
        opacity={0.2}
      />
    );
  }

  // Carbon cycle arrows
  const carbonCycle = [];
  const cycleRadius = 220;
  for (let i = 0; i < 12; i++) {
    const angle = (i * Math.PI * 2) / 12;
    const x1 = 397 + cycleRadius * Math.cos(angle);
    const y1 = 561.5 + cycleRadius * Math.sin(angle);
    const angle2 = ((i + 1) * Math.PI * 2) / 12;
    const x2 = 397 + cycleRadius * Math.cos(angle2);
    const y2 = 561.5 + cycleRadius * Math.sin(angle2);
    carbonCycle.push(
      <line
        key={`carbon-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke={`url(#${gradId})`}
        strokeWidth={2}
        opacity={0.5}
        markerEnd={`url(#${arrowId})`}
      />
    );
  }

  // Central climate core
  const climateCore = (
    <g opacity={0.9}>
      <circle cx={397} cy={561.5} r={120} fill="url(#climateGlow)" />
      <circle cx={397} cy={561.5} r={80} fill="none" stroke="#F44336" strokeWidth={3} />
      <circle cx={397} cy={561.5} r={60} fill="none" stroke="#FFC107" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={40} fill="none" stroke="#4CAF50" strokeWidth={2} />
      <circle cx={397} cy={561.5} r={20} fill="none" stroke="#00BCD4" strokeWidth={2} />
      <text
        x={397}
        y={575}
        fontFamily="monospace"
        fontSize={20}
        fontWeight="bold"
        fill="#FFFFFF"
        textAnchor="middle"
      >
        1.5°C
      </text>
    </g>
  );

  return (
    <g>
      {defs}
      {cryosphereGrids}
      {heatmapGradients}
      {circulationLoops}
      {carbonCycle}
      {climateCore}

      {/* Render Copy object fields (not a component) */}
      <g className="mode-copy" transform="translate(48, 840)" fill="#FFFFFF" fontFamily="monospace">
        <text x={0} y={0} fontSize={18} fontWeight="bold">
          {Copy?.title}
        </text>
        <text x={0} y={22} fontSize={14}>
          {Copy?.subtitle}
        </text>
        <text x={0} y={44} fontSize={12} opacity={0.85}>
          {Copy?.date}
        </text>
      </g>
    </g>
  );
}
